import React, { useContext, useState, useEffect, useCallback } from "react";
import { Switch } from "react-router-dom";
import FormsContext from "./FormsContext";
import AppContext from "AppContext";
import PrivateRoute from "components/PrivateRoute";
import List from "./List";
import AddWizard from "./AddWizard";
import AddForm from "./AddForm";
import ViewForm from "./ViewForm";
import EditForm from "./EditForm";
import CopyToVersionForm from "./CopyToVersionForm";
import { SearchBy } from "./components/SearchByToggle";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import { getFormPdfLocation } from "./utils";

function Forms() {
    const dispatch = useDispatch();
    const [isLoadingResources, setIsLoadingResources] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [resources, setResources] = useState({
        forms: [],
        issuers: [],
        categories: [],
    });
    const [addFormState, setAddFormState] = useState({
        category: undefined,
        form: undefined,
        client: undefined,
        // undefined = not decided yet, null = no case
        fileCase: undefined,
        searchBy: SearchBy.Client,
        attorneyUser: null,
    });

    const {
        api: {
            Forms: { FormClients },
            General: { Storage },
            Management: { Customers },
            Settings: {
                Users,
                InvoicingIssuers,
                Forms: { Forms, Issuers, Categories },
            },
        },
    } = useContext(AppContext);

    const resetAddFormState = () => {
        setAddFormState((prev) => ({
            ...prev,
            category: undefined,
            form: undefined,
            client: undefined,
            fileCase: undefined,
            attorneyUser: null,
        }));
        // Sin esto la siguiente pantalla monta con el PDF del formulario anterior.
        setPdfUrl(null);
    };

    const getForms = useCallback(() => {
        setIsLoadingResources(true);
        Forms.getCurrent({
            onSuccess: (forms) => {
                setResources((prev) => ({
                    ...prev,
                    forms,
                }));
                setIsLoadingResources(false);
            },
            onError: (error) => {
                setResources((prev) => ({ ...prev, forms: [] }));
                dispatch(notifyError(error));
                setIsLoadingResources(false);
            },
        });
    }, [Forms, dispatch]);

    const getIssuers = useCallback(() => {
        setIsLoadingResources(true);
        Issuers.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    issuers: data,
                }));
                setIsLoadingResources(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    issuers: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingResources(false);
            },
        });
    }, [Issuers, dispatch]);

    const getCategories = useCallback(() => {
        setIsLoadingResources(true);
        Categories.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    categories: data,
                }));
                setIsLoadingResources(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    categories: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingResources(false);
            },
        });
    }, [Categories, dispatch]);

    const getUsers = useCallback(() => {
        setIsLoadingResources(true);
        Users.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    users: data,
                }));
                setIsLoadingResources(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    users: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingResources(false);
            },
        });
    }, [Users, dispatch]);

    useEffect(() => {
        getUsers();
        getForms();
        getIssuers();
        getCategories();
    }, [getForms, getIssuers, getCategories, getUsers]);

    const setStatus = async (id, status, callback) => {
        try {
            const response = await FormClients.setStatusAsync({ id, status });
            callback(response.data);
            dispatch(notifySuccess("Status changed."));
        } catch (error) {
            dispatch(notifyError(error.response?.data?.message || error.message || "Unexpected error."));
        }
    };

    const getAttorneyUsersOptions = () => {
        return (resources.users || []).filter((x) => x.isAttorney);
    };

    async function getFileUrlAsync(form) {
        try {
            const fUrl = await Storage.getDownloadURLAsync(getFormPdfLocation(form));
            setPdfUrl(fUrl);
        } catch (message) {}
    }

    return (
        <FormsContext.Provider
            value={{
                resources,
                isLoadingResources,
                FormClients,
                Storage,
                Customers,
                addFormState,
                setAddFormState,
                resetAddFormState,
                setStatus,
                getAttorneyUsersOptions,
                getFileUrlAsync,
                pdfUrl,
                InvoicingIssuers,
                Forms,
            }}
        >
            <Switch>
                <PrivateRoute exact path="/forms" component={List} />
                <PrivateRoute exact path="/forms/add/wizard" component={AddWizard} />
                <PrivateRoute exact path="/forms/add" component={AddForm} />
                <PrivateRoute exact path="/forms/:id" component={ViewForm} />
                <PrivateRoute exact path="/forms/:id/edit" component={EditForm} />
                <PrivateRoute exact path="/forms/:id/copy-to-current-version" component={CopyToVersionForm} />
            </Switch>
        </FormsContext.Provider>
    );
}

export default Forms;
